import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cart } from "./cart";
import { listInput, listPage } from "./list";
import { Cents } from "./money";
import { OrderLine, OrderStatus, PaymentStatus, ShipmentStatus, ShopOrder } from "./order";
import { VatBasis } from "./price";

/**
 * The signed-in customer's orders in the shop (#105: "Bestelhistorie", #106: "Opnieuw bestellen").
 * Every endpoint needs the customer session (401 `CustomerNotSignedIn`).
 *
 * - Only the customer's own orders: the ones placed while signed in. An order placed as a guest,
 *   even with the same email address, belongs to that guest's customer and is not listed.
 * - An order is addressed by its number (`CBO-100000`). Someone else's order, or a number that
 *   does not exist, is `AccountOrderNotFound` (404), the same answer for both.
 */

/** The page of the history: newest first. Only `page` and `pageSize`, as every list takes them. */
export const AccountOrderListInput = listInput({ sort: ["placedAt"], defaultSort: "placedAt" }).pick({
	page: true,
	pageSize: true,
});
export type AccountOrderListInput = z.infer<typeof AccountOrderListInput>;

/** One order in the history. */
export const AccountOrderSummary = z.object({
	orderNumber: z.string(),
	placedAt: z.iso.datetime(),
	paymentStatus: PaymentStatus,
	shipmentStatus: ShipmentStatus,
	orderStatus: OrderStatus,
	/** What the order came to, including VAT (the order's `totals.toPay`). */
	total: Cents,
	/** The side of VAT the order's prices are on; `total` is always including VAT. */
	priceDisplay: VatBasis,
	/** How many lines (products) the order has, not units. */
	lineCount: z.number().int(),
});
export type AccountOrderSummary = z.infer<typeof AccountOrderSummary>;

export const AccountOrderList = listPage(AccountOrderSummary);
export type AccountOrderList = z.infer<typeof AccountOrderList>;

/** PostNL, DHL, DPD or Bpost (spec, "Vervoerders"), as the orders admin stores it. */
export const AccountOrderTracking = z.object({
	carrier: z.string(),
	code: z.string(),
	/** The carrier's track-and-trace page for this parcel; `null` for a carrier we have no link for. */
	url: z.url().nullable(),
});
export type AccountOrderTracking = z.infer<typeof AccountOrderTracking>;

/** An order's line in the account: the snapshot, plus where the product is in the shop today. */
export const AccountOrderLine = OrderLine.extend({
	/**
	 * The product's slug (`/product/{slug}`) while the line's variant is for sale (active, not
	 * deleted, of an active product); `null` once it is not. The shop reads the size label from it.
	 */
	productSlug: z.string().nullable(),
});
export type AccountOrderLine = z.infer<typeof AccountOrderLine>;

/** An order as the customer sees it in their account: the snapshot, plus tracking and invoice. */
export const AccountOrder = ShopOrder.extend({
	lines: z.array(AccountOrderLine),
	/** Once shipped with a tracking code; `null` before. */
	tracking: AccountOrderTracking.nullable(),
	/**
	 * Where the invoice pdf downloads (`invoice` below), once the order has one; `null` before. Only
	 * with the customer's own session.
	 */
	invoiceUrl: z.string().nullable(),
});
export type AccountOrder = z.infer<typeof AccountOrder>;

/** An order's place in the path: `/shop/account/orders/{orderNumber}`. */
export const AccountOrderPath = z.object({ orderNumber: z.string().min(1) });

export const AccountOrderContract = {
	/** The customer's orders, newest first. */
	list: oc
		.route({ method: "GET", path: "/shop/account/orders" })
		.input(AccountOrderListInput)
		.output(AccountOrderList),
	/** One of the customer's orders. Errors: `AccountOrderNotFound` (404). */
	get: oc
		.route({ method: "GET", path: "/shop/account/orders/{orderNumber}" })
		.input(AccountOrderPath)
		.output(AccountOrder),
	/**
	 * Puts the order's lines in the customer's cart, and answers the cart (as `POST /shop/cart/lines`
	 * would, at today's prices). Per line:
	 *
	 * - a variant that is deleted, inactive or of an inactive product is skipped, with a
	 *   `variantRemoved` notice (with the product name);
	 * - the quantity adds up with what the cart already holds, but never past what is available
	 *   now, as when a guest cart is merged: a capped line gets a `quantityReduced` notice with the
	 *   quantity the cart holds now; `quantity: 0` means the product is out of stock and was not
	 *   added.
	 *
	 * Errors: `AccountOrderNotFound` (404).
	 */
	reorder: oc
		.route({ method: "POST", path: "/shop/account/orders/{orderNumber}/reorder" })
		.input(AccountOrderPath)
		.output(Cart),
	/**
	 * The order's invoice as a pdf (the order's `invoiceUrl`). Errors: `InvoiceNotFound` (404): the
	 * order has no invoice yet, or is not the customer's.
	 */
	invoice: oc
		.route({ method: "GET", path: "/shop/account/orders/{orderNumber}/invoice" })
		.input(AccountOrderPath)
		.output(z.file()),
};
